import { supabase } from './supabase';

/**
 * Tag Approval — data layer.
 *
 * ══════════════════════════════════════════════════════════════════════════════
 * ⚠️ READ THIS BEFORE CHANGING ANY DATABASE NAME IN THIS FILE ⚠️
 *
 * This dashboard does NOT own the tag-moderation schema. It is being written in
 * the app repo (`~/shado/supabase/migrations`). As of the last verification pass
 * (2026-09-09, `information_schema` + `pg_proc` against production) the state was:
 *
 *   PRESENT  public.custom_thought_tags                 table, 0 rows, RLS on
 *            └─ id, user_id, venue_id, criterion, tag, tag_key, created_at
 *            └─ NO status / approved_at / reviewed_by column — it is an ANALYTICS
 *               table ("what did people type"), not a moderation queue
 *   PRESENT  public.admin_list_custom_thought_tags(int,int)   EXECUTE → authenticated
 *            └─ criterion, tag, times_added, distinct_users, distinct_venues,
 *               first_seen, last_seen  — gated internally by public.is_admin()
 *   PRESENT  public.venue_portal_tag_catalogue()             EXECUTE → authenticated
 *            └─ the built-in catalogue jsonb (app_config `venue_thought_tag_catalogue`)
 *   ABSENT   any pending/approved state, any alias table, any decide RPC
 *
 * So EVERY database object this feature talks to is named in {@link TAG_MODERATION_BINDING}
 * below and NOWHERE else in this file or in `TagApprovalScreen.tsx`. The three
 * moderation names in there are PROPOSALS, not assumptions: nothing is ever called
 * without first probing for it, and a missing object puts the whole screen into a
 * clearly-labelled read-only Preview instead of erroring or inventing data.
 *
 * When the schema lands: run the probe (open the screen), and if the real names
 * differ, change the three strings in the binding. Nothing else moves.
 * ══════════════════════════════════════════════════════════════════════════════
 *
 * ── The product rules this layer encodes (Evan, 2026-09-09) ───────────────────
 *  1. Only FREE-TYPED new tags need approval. Catalogue tags apply immediately and
 *     never enter this queue.
 *  2. While pending, the author sees their own tag; nobody else does.
 *  3. Editing creates a PERMANENT ALIAS. "rooftop bar" → "rooftop" means every future
 *     submission of "rooftop bar" resolves to "rooftop" and never re-enters the queue.
 *     The author's post is NEVER rewritten — they keep the words they typed; the alias
 *     is what makes their submission count toward the canonical tag.
 *
 * ── Untrusted input ──────────────────────────────────────────────────────────
 * Every `tag` string here was typed by a user. It is rendered in an admin UI, so:
 *   • It is only ever placed in a React text node or a `title` attribute — both are
 *     escaped by React. There is no `dangerouslySetInnerHTML` anywhere in this feature.
 *   • It is NEVER interpolated into a PostgREST filter (`.ilike()`, `.or()`), because
 *     all filtering/searching in this feature is client-side over rows already fetched.
 *     Every write goes through `supabase.rpc()`, whose arguments are a JSON body — the
 *     parameterised path, not string-built SQL.
 */

/* ────────────────────────────────────────────────────────────────────────── */
/* Binding — the ONLY place a database object is named                        */
/* ────────────────────────────────────────────────────────────────────────── */

export const TAG_MODERATION_BINDING = {
  /** VERIFIED PRESENT 2026-09-09. Aggregate over the typed-tag corpus. Powers Preview. */
  aggregateRpc: 'admin_list_custom_thought_tags',

  /** VERIFIED PRESENT 2026-09-09. Built-in catalogue, keyed by criterion. */
  catalogueRpc: 'venue_portal_tag_catalogue',

  /**
   * ⛔ NOT PRESENT 2026-09-09 — proposed name, probed before every use.
   * Expected: `(p_limit int, p_offset int)` → SETOF, one row per PENDING submission,
   * shaped like {@link PendingTagRow}. Must be `SECURITY DEFINER` + `is_admin()`-gated,
   * `EXECUTE` granted to `authenticated` (the dashboard holds only the anon key).
   */
  queueRpc: 'admin_list_pending_thought_tags',

  /**
   * ⛔ NOT PRESENT 2026-09-09 — proposed name, probed before use. OPTIONAL:
   * if it never exists, duplicate detection still runs against the catalogue and the
   * rest of the queue, and the screen says so rather than pretending it is complete.
   * Expected: `()` → SETOF `(criterion text, tag text)` — the already-approved custom
   * vocabulary (canonical forms only, aliases excluded).
   */
  approvedRpc: 'admin_list_approved_thought_tags',

  /**
   * ⛔ NOT PRESENT 2026-09-09 — proposed name, probed before use.
   * Expected: `(p_ids uuid[], p_action text, p_canonical text)`.
   *   p_action 'approve' + p_canonical NULL  → approve exactly as typed
   *   p_action 'approve' + p_canonical set   → approve AS the canonical form and record a
   *                                            permanent alias raw_key → canonical.
   *                                            MUST NOT touch `logs.criteria_thought_tags`.
   *   p_action 'reject'                      → reject; never re-queues.
   * MUST be idempotent: deciding an already-decided id is a no-op that succeeds, so a
   * double-click, a retry, or two admins racing cannot double-apply.
   */
  decideRpc: 'admin_decide_thought_tag',
} as const;

/* ────────────────────────────────────────────────────────────────────────── */
/* Criteria                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * The six rating criteria, in the app's own order (`lib/criteriaThoughtTags.ts`).
 *
 * NOT an enum in the database and deliberately not a closed set here either: the
 * migration's own note explains the labels have been renamed before (`vibe`, `crowd`,
 * `door`, `lineup`, `sound` are still handled as legacy aliases in the app). Anything
 * the database returns that is not in this list is shown under "Other" rather than
 * dropped — an unexpected label is a data-quality annoyance, never a reason to hide
 * a row from the person whose job is to look at it.
 */
export const CRITERIA = ['Venue', 'Atmosphere', 'Music', 'Drinks', 'Production', 'Service'] as const;
export type Criterion = (typeof CRITERIA)[number];

export function criterionRank(c: string): number {
  const i = (CRITERIA as readonly string[]).indexOf(c);
  return i === -1 ? CRITERIA.length : i;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Normalisation ladder                                                       */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Level 1 — case + accent fold, whitespace collapsed.
 *
 * Mirrors the database's own `tag_key` exactly: `btrim(unaccent(lower(tag)))` with the
 * BEFORE-INSERT trigger's `regexp_replace(tag, '\s+', ' ')` applied first
 * (`20260867000000_custom_thought_tags.sql`). Two strings with the same `foldKey` are the
 * SAME TAG as far as the database is concerned — "Rooftop", "rooftop" and "ROOFTOP" are
 * one row's worth of meaning, and one of them is already in the corpus.
 *
 * The NFD + combining-mark strip is wrapped in try/catch to match the app's own
 * `ThoughtTagSearchRow` helper: an engine without `normalize` degrades to plain
 * case-insensitive matching, which is still useful, rather than throwing.
 */
export function foldKey(s: string): string {
  const collapsed = s.replace(/\s+/g, ' ').trim().toLowerCase();
  try {
    return collapsed.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
  } catch {
    return collapsed;
  }
}

/**
 * Level 2 — every non-alphanumeric character removed.
 *
 * This is the level that catches the variant the database CANNOT see: "roof top",
 * "roof-top" and "rooftop" have three different `tag_key`s and are three separate rows,
 * but one idea. Same for "hip-hop"/"hip hop" and "LGBTQ+"/"lgbtq".
 */
export function foldTight(s: string): string {
  return foldKey(s).replace(/[^a-z0-9]/g, '');
}

/**
 * Naive English singularisation of one token. Deliberately conservative — it only
 * strips endings that are overwhelmingly plural, and refuses on short words where the
 * "s" is far more likely to be part of the word ("gas", "jazz", "class", "vibes" is
 * intentionally NOT protected because "vibe"/"vibes" really is the collision we want).
 */
function singularise(w: string): string {
  if (w.length <= 3) return w;
  if (/[^aeiou]ies$/.test(w)) return `${w.slice(0, -3)}y`; // parties → party
  if (/(ss|sh|ch|x|z)es$/.test(w)) return w.slice(0, -2); // brunches → brunch
  if (/(ss|us|is)$/.test(w)) return w; // class, focus, oasis — leave alone
  if (w.endsWith('s')) return w.slice(0, -1); // rooftops → rooftop, lights → light
  return w;
}

/**
 * Level 3 — level 2 plus per-word singularisation.
 *
 * "rooftops" vs "rooftop", "lasers" vs "laser", "shots" vs "shot". Note several BUILT-IN
 * catalogue entries are already plural ("lights", "lasers", "strobes", "shots", "cocktails"),
 * so this level is what stops someone approving the singular of a tag the app already ships.
 */
export function foldStem(s: string): string {
  return foldKey(s)
    .split(/[^a-z0-9]+/)
    .filter(Boolean)
    .map(singularise)
    .join('');
}

/** Levenshtein distance, capped — returns `cap + 1` as soon as it is certain to exceed. */
export function editDistance(a: string, b: string, cap: number): number {
  if (a === b) return 0;
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      const v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
      cur.push(v);
      if (v < rowMin) rowMin = v;
    }
    if (rowMin > cap) return cap + 1;
    prev = cur;
  }
  return prev[b.length];
}

/**
 * How far apart two tight-folded strings may be and still be called a typo.
 *
 * Scaled by length because a one-character edit means something completely different at
 * four characters than at twelve: "bar"/"car" and "pub"/"pun" are one edit apart and are
 * not the same word, while "resturant"/"restaurant" obviously is. Below five characters
 * fuzzy matching is switched off entirely.
 */
function typoBudget(len: number): number {
  if (len <= 4) return 0;
  if (len <= 8) return 1;
  return 2;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Duplicate detection                                                        */
/* ────────────────────────────────────────────────────────────────────────── */

/** Where a colliding tag already lives. */
export type MatchSource = 'catalogue' | 'approved' | 'pending';

/** Why two strings collided, strongest first. Drives the copy and the badge colour. */
export type MatchKind = 'exact' | 'punctuation' | 'plural' | 'typo' | 'contains';

export type TagMatch = {
  /** The EXISTING string — the canonical form Evan would normally fold the new one into. */
  existing: string;
  source: MatchSource;
  kind: MatchKind;
  /** The criterion the existing tag lives under. */
  criterion: string;
  /** False when the existing tag lives under a DIFFERENT criterion than the submission. */
  sameCriterion: boolean;
};

const KIND_RANK: Record<MatchKind, number> = {
  exact: 0,
  punctuation: 1,
  plural: 2,
  typo: 3,
  contains: 4,
};

export const MATCH_COPY: Record<MatchKind, string> = {
  exact: 'same tag, different casing',
  punctuation: 'same letters, different spacing or punctuation',
  plural: 'singular/plural of an existing tag',
  typo: 'one or two characters from an existing tag',
  contains: 'contains an existing tag',
};

/** One candidate the incoming string is compared against. */
export type VocabEntry = { tag: string; criterion: string; source: MatchSource };

/**
 * Compare one submitted string against the whole known vocabulary.
 *
 * Returns matches sorted strongest-first, same-criterion before cross-criterion. A
 * same-criterion `exact` is the loudest possible signal (the tag already exists verbatim);
 * a cross-criterion hit is a note, not a blocker — the migration's reasoning applies:
 * "unique" under Drinks and "unique" under Music are genuinely two different asks.
 */
export function findMatches(tag: string, criterion: string, vocab: readonly VocabEntry[]): TagMatch[] {
  const key = foldKey(tag);
  const tight = foldTight(tag);
  const stem = foldStem(tag);
  if (!tight) return [];
  const budget = typoBudget(tight.length);

  const out: TagMatch[] = [];
  const seen = new Set<string>();

  for (const entry of vocab) {
    const eKey = foldKey(entry.tag);
    const eTight = foldTight(entry.tag);
    if (!eTight) continue;
    // The string compared against itself is not a duplicate of itself.
    if (eKey === key && entry.criterion === criterion && entry.source === 'pending') continue;

    let kind: MatchKind | null = null;
    if (eKey === key) kind = 'exact';
    else if (eTight === tight) kind = 'punctuation';
    else if (foldStem(entry.tag) === stem) kind = 'plural';
    else if (budget > 0 && editDistance(tight, eTight, budget) <= budget) kind = 'typo';
    else if (
      // Substring only counts when the shorter side is a real word, not a fragment —
      // otherwise "bar" matches half the catalogue and the warning becomes noise.
      Math.min(tight.length, eTight.length) >= 4 &&
      (tight.includes(eTight) || eTight.includes(tight))
    )
      kind = 'contains';

    if (!kind) continue;

    const dedupe = `${entry.source}:${entry.criterion}:${eKey}`;
    if (seen.has(dedupe)) continue;
    seen.add(dedupe);

    out.push({
      existing: entry.tag,
      source: entry.source,
      kind,
      criterion: entry.criterion,
      sameCriterion: entry.criterion === criterion,
    });
  }

  out.sort((a, b) => {
    if (a.sameCriterion !== b.sameCriterion) return a.sameCriterion ? -1 : 1;
    if (KIND_RANK[a.kind] !== KIND_RANK[b.kind]) return KIND_RANK[a.kind] - KIND_RANK[b.kind];
    return a.existing.localeCompare(b.existing);
  });
  return out;
}

/** The single match worth putting on the row, if any. Same-criterion always wins. */
export function primaryMatch(matches: readonly TagMatch[]): TagMatch | null {
  return matches.find((m) => m.sameCriterion) ?? matches[0] ?? null;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Catalogue                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Flatten the catalogue jsonb into `VocabEntry`s.
 *
 * The shape is NOT uniform and a flat-array assumption silently loses 78 tags: five
 * criteria map to a string array, but "Venue" maps to an object keyed by venue type
 * (`{"bar":[…],"club":[…],"event":[…]}`). Confirmed against production
 * `app_config.venue_thought_tag_catalogue` on 2026-09-09.
 */
export function flattenCatalogue(raw: unknown): VocabEntry[] {
  const out: VocabEntry[] = [];
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  const seen = new Set<string>();

  const push = (criterion: string, tag: unknown) => {
    if (typeof tag !== 'string') return;
    const t = tag.trim();
    if (!t) return;
    const dedupe = `${criterion}:${foldKey(t)}`;
    if (seen.has(dedupe)) return;
    seen.add(dedupe);
    out.push({ tag: t, criterion, source: 'catalogue' });
  };

  for (const [criterion, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Array.isArray(value)) {
      for (const t of value) push(criterion, t);
    } else if (value && typeof value === 'object') {
      // "Venue" → { bar: [...], club: [...], event: [...] }
      for (const nested of Object.values(value as Record<string, unknown>)) {
        if (Array.isArray(nested)) for (const t of nested) push(criterion, t);
      }
    }
  }
  return out;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Row types                                                                  */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * One pending submission, as the screen needs it.
 *
 * `distinctUsers` is the column that changes the decision, not decoration: a string five
 * different people typed is a demand signal; the same string typed five times by one
 * person is one person. `admin_list_custom_thought_tags` already computes exactly this
 * (`count(DISTINCT c.user_id)`), so the live queue RPC should expose the same number
 * per submission rather than making the client re-derive it.
 */
export type PendingTagRow = {
  /** Stable id used for selection, in-flight tracking and the decide call. */
  id: string;
  /** As typed, after the trigger's trim + whitespace collapse. UNTRUSTED. */
  tag: string;
  criterion: string;
  venueId: string | null;
  venueName: string | null;
  city: string | null;
  submittedBy: string | null;
  submittedByUsername: string | null;
  createdAt: string | null;
  /** How many DIFFERENT people have submitted this same string. */
  distinctUsers: number;
  /** Total submissions of this string, one person or many. */
  timesAdded: number;
  /** How many distinct venues it has been applied to. */
  distinctVenues: number;
  /**
   * True when the row came from the Preview aggregate rather than a real moderation
   * queue. Every action refuses on these — there is nothing to decide yet.
   */
  previewOnly: boolean;
};

export type QueueMode = 'live' | 'preview';

export type TagWorkspace = {
  mode: QueueMode;
  rows: PendingTagRow[];
  /** Catalogue + approved custom tags — what a submission is checked against. */
  vocab: VocabEntry[];
  /** True when the approved-vocabulary source was reachable. */
  approvedVocabLoaded: boolean;
  /** Human-readable reason the screen is in Preview, or null when live. */
  previewReason: string | null;
};

/* ────────────────────────────────────────────────────────────────────────── */
/* Probing                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * A PostgREST "that function does not exist", as opposed to a real failure.
 *
 * PostgREST reports a missing RPC as PGRST202, and Postgres as 42883. Matching the
 * `collectionKinds.rpcMissing` convention already used on the Collections screen so a
 * not-yet-built backend reads as a quiet set-up note, never a red banner.
 */
export function rpcMissing(message: string | null | undefined, fn: string): boolean {
  const m = (message ?? '').toLowerCase();
  return (
    m.includes('pgrst202') ||
    m.includes('42883') ||
    m.includes('could not find the function') ||
    m.includes('does not exist') ||
    m.includes('schema cache') ||
    (m.includes(fn.toLowerCase()) && m.includes('not find'))
  );
}

/** A row is only a moderation row if the payload actually carries an id to decide on. */
function hasDecidableId(r: Record<string, unknown>): boolean {
  const id = r.id ?? r.tag_id ?? r.submission_id;
  return typeof id === 'string' && id.length > 0;
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v : null;
}

function num(v: unknown, fallback = 0): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

/**
 * Map a live queue row. Tolerant on names because this dashboard does not own the schema:
 * `venue_name`/`name`, `submitted_by`/`user_id`, `username`/`submitted_by_username` all
 * work. The one field with no fallback is the id — a payload without one cannot be
 * decided on, and is rejected by {@link hasDecidableId} rather than given a fake id.
 */
function mapQueueRow(raw: Record<string, unknown>): PendingTagRow | null {
  if (!hasDecidableId(raw)) return null;
  const tag = str(raw.tag) ?? str(raw.tag_text);
  if (!tag) return null;
  return {
    id: String(raw.id ?? raw.tag_id ?? raw.submission_id),
    tag,
    criterion: str(raw.criterion) ?? 'Other',
    venueId: str(raw.venue_id),
    venueName: str(raw.venue_name) ?? str(raw.name) ?? str(raw.venue_name_user_input),
    city: str(raw.city) ?? str(raw.city_country),
    submittedBy: str(raw.submitted_by) ?? str(raw.user_id),
    submittedByUsername: str(raw.submitted_by_username) ?? str(raw.username),
    createdAt: str(raw.created_at) ?? str(raw.submitted_at),
    distinctUsers: num(raw.distinct_users, 1),
    timesAdded: num(raw.times_added, 1),
    distinctVenues: num(raw.distinct_venues, raw.venue_id ? 1 : 0),
    previewOnly: false,
  };
}

/** Map an `admin_list_custom_thought_tags` aggregate row into a Preview row. */
function mapAggregateRow(raw: Record<string, unknown>): PendingTagRow | null {
  const tag = str(raw.tag);
  if (!tag) return null;
  const criterion = str(raw.criterion) ?? 'Other';
  return {
    // Synthetic and clearly marked — never sent to a decide call (`previewOnly` refuses first).
    id: `preview:${criterion}:${foldKey(tag)}`,
    tag,
    criterion,
    venueId: null,
    venueName: null,
    city: null,
    submittedBy: null,
    submittedByUsername: null,
    createdAt: str(raw.last_seen),
    distinctUsers: num(raw.distinct_users, 0),
    timesAdded: num(raw.times_added, 0),
    distinctVenues: num(raw.distinct_venues, 0),
    previewOnly: true,
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Loading                                                                    */
/* ────────────────────────────────────────────────────────────────────────── */

/** The catalogue — the vocabulary a submission must not silently duplicate. */
async function loadCatalogue(): Promise<VocabEntry[]> {
  const { data, error } = await supabase.rpc(TAG_MODERATION_BINDING.catalogueRpc);
  if (error) return [];
  return flattenCatalogue(data);
}

/** Already-approved custom tags. Optional source — absent is a degraded, disclosed state. */
async function loadApprovedVocab(): Promise<{ entries: VocabEntry[]; ok: boolean }> {
  const { data, error } = await supabase.rpc(TAG_MODERATION_BINDING.approvedRpc);
  if (error) return { entries: [], ok: false };
  const rows = Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
  const entries: VocabEntry[] = [];
  for (const r of rows) {
    const tag = str(r.tag);
    if (!tag) continue;
    entries.push({ tag, criterion: str(r.criterion) ?? 'Other', source: 'approved' });
  }
  return { entries, ok: true };
}

const QUEUE_PAGE = 500;

/**
 * Load the workspace, probing for the moderation backend first.
 *
 * Live path: {@link TAG_MODERATION_BINDING.queueRpc}. Preview path: the aggregate that is
 * verified to exist. The choice is made from what the database ANSWERS, never from a
 * hardcoded flag — so the day the schema lands this screen becomes live on the next
 * refresh with no code change.
 *
 * A `queueRpc` that exists but errors for a real reason (permission, a bug) is NOT
 * silently downgraded to Preview: that would hide a broken queue behind a screen that
 * looks like it is working. It throws, and the screen shows the error.
 */
export async function loadTagWorkspace(): Promise<TagWorkspace> {
  const [catalogue, approved] = await Promise.all([loadCatalogue(), loadApprovedVocab()]);

  const queue = await supabase.rpc(TAG_MODERATION_BINDING.queueRpc, {
    p_limit: QUEUE_PAGE,
    p_offset: 0,
  });

  if (!queue.error) {
    const raw = Array.isArray(queue.data) ? (queue.data as Record<string, unknown>[]) : [];
    const rows = raw.map(mapQueueRow).filter((r): r is PendingTagRow => r !== null);
    // The RPC answered but nothing in the payload is decidable — the shape is not a
    // moderation queue. Say so rather than rendering an empty "queue is clear".
    if (raw.length > 0 && rows.length === 0) {
      return previewWorkspace(
        catalogue,
        approved,
        `\`${TAG_MODERATION_BINDING.queueRpc}\` answered, but no row carried an id to decide on — the payload is not a moderation queue yet.`,
      );
    }
    return {
      mode: 'live',
      rows,
      vocab: [...catalogue, ...approved.entries],
      approvedVocabLoaded: approved.ok,
      previewReason: null,
    };
  }

  if (!rpcMissing(queue.error.message, TAG_MODERATION_BINDING.queueRpc)) {
    throw new Error(queue.error.message);
  }

  return previewWorkspace(
    catalogue,
    approved,
    `\`${TAG_MODERATION_BINDING.queueRpc}\` does not exist in production yet.`,
  );
}

async function previewWorkspace(
  catalogue: VocabEntry[],
  approved: { entries: VocabEntry[]; ok: boolean },
  reason: string,
): Promise<TagWorkspace> {
  const agg = await supabase.rpc(TAG_MODERATION_BINDING.aggregateRpc, {
    p_limit: QUEUE_PAGE,
    p_offset: 0,
  });
  if (agg.error) {
    // The aggregate is verified to exist, so a failure here is real — usually "forbidden:
    // admin only" from a non-admin session. Surface it.
    throw new Error(agg.error.message);
  }
  const raw = Array.isArray(agg.data) ? (agg.data as Record<string, unknown>[]) : [];
  return {
    mode: 'preview',
    rows: raw.map(mapAggregateRow).filter((r): r is PendingTagRow => r !== null),
    vocab: [...catalogue, ...approved.entries],
    approvedVocabLoaded: approved.ok,
    previewReason: reason,
  };
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Deciding                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

export type DecideAction = 'approve' | 'reject';

export type DecideInput = {
  ids: string[];
  action: DecideAction;
  /**
   * The canonical form for an edit-then-approve. `null` approves exactly as typed.
   *
   * Non-null means: approve these submissions AS this string, and record a permanent alias
   * from each submitted string to it, so future submissions of the old spelling resolve
   * here and never re-enter the queue. The author's post keeps the words they typed — the
   * alias is what makes their submission count toward the canonical tag. That contract
   * lives in the RPC; this layer only carries the intent.
   */
  canonical: string | null;
};

export class TagModerationUnavailable extends Error {}

/**
 * Apply one decision.
 *
 * ⚠️ IDEMPOTENCY. The decide RPC is specified to be idempotent (deciding an
 * already-decided id succeeds as a no-op), but the client does not lean on that alone —
 * `TagApprovalScreen` holds an in-flight id set and a decided id set, and refuses to
 * dispatch an id that is in either. Two independent guards, because the failure this
 * prevents (a double-click creating two aliases, or an approve landing after a reject)
 * is silent and permanent.
 *
 * The call itself is state-SETTING, never incrementing — `p_action` names the end state,
 * so replaying it converges rather than accumulating.
 */
export async function decideTags(input: DecideInput): Promise<void> {
  const ids = Array.from(new Set(input.ids.filter(Boolean)));
  if (ids.length === 0) return;
  if (ids.some((id) => id.startsWith('preview:'))) {
    throw new TagModerationUnavailable('These rows are from the read-only preview — there is nothing to decide yet.');
  }

  const canonical = input.canonical == null ? null : input.canonical.replace(/\s+/g, ' ').trim();
  if (input.canonical != null && !canonical) {
    throw new Error('The canonical tag cannot be empty.');
  }

  const { error } = await supabase.rpc(TAG_MODERATION_BINDING.decideRpc, {
    p_ids: ids,
    p_action: input.action,
    p_canonical: canonical,
  });

  if (error) {
    if (rpcMissing(error.message, TAG_MODERATION_BINDING.decideRpc)) {
      throw new TagModerationUnavailable(
        `\`${TAG_MODERATION_BINDING.decideRpc}\` does not exist yet — the tag-moderation schema has not been applied.`,
      );
    }
    throw new Error(error.message);
  }
}

/**
 * Longest a canonical tag may be.
 *
 * 24, matching the database trigger (`char_length(v_tag) > 24` raises
 * `custom_thought_tag_too_long`) and the app's own `CUSTOM_THOUGHT_TAG_MAX_LENGTH`. An
 * edit that exceeds it would be rejected by the trigger, so the input refuses first and
 * says why, rather than letting the write fail with a raw SQLSTATE.
 */
export const CANONICAL_MAX_LENGTH = 24;

export function validateCanonical(value: string): string | null {
  const t = value.replace(/\s+/g, ' ').trim();
  if (!t) return 'Enter the canonical tag.';
  if (t.length > CANONICAL_MAX_LENGTH) return `${CANONICAL_MAX_LENGTH} characters max — this is ${t.length}.`;
  return null;
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Clustering                                                                 */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Group the queue into near-duplicate clusters so a long tail of variants can be handled
 * in one gesture instead of twenty.
 *
 * Union-find over `foldStem` within a criterion — the level that already collapses casing,
 * punctuation and plurals. Typo-distance is deliberately NOT used to form clusters: it is
 * good enough to WARN on a single row, but transitive typo-merging chains unrelated words
 * together ("bar" → "car" → "cars") and a bulk action built on a bad chain is not
 * recoverable by looking at the screen.
 */
export function clusterKey(row: PendingTagRow): string {
  return `${row.criterion}::${foldStem(row.tag)}`;
}

export function buildClusters(rows: readonly PendingTagRow[]): Map<string, PendingTagRow[]> {
  const map = new Map<string, PendingTagRow[]>();
  for (const r of rows) {
    const k = clusterKey(r);
    const list = map.get(k);
    if (list) list.push(r);
    else map.set(k, [r]);
  }
  return map;
}

/**
 * The spelling a cluster should collapse to: the one the most DIFFERENT people typed,
 * then the most submissions, then the shortest. Never a folded slug — always a string a
 * real person actually wrote, which is the same choice `admin_list_custom_thought_tags`
 * makes when it returns "the most recent spelling for this folded key".
 */
export function suggestCanonical(rows: readonly PendingTagRow[]): string {
  const best = [...rows].sort((a, b) => {
    if (b.distinctUsers !== a.distinctUsers) return b.distinctUsers - a.distinctUsers;
    if (b.timesAdded !== a.timesAdded) return b.timesAdded - a.timesAdded;
    if (a.tag.length !== b.tag.length) return a.tag.length - b.tag.length;
    return a.tag.localeCompare(b.tag);
  })[0];
  return best?.tag ?? '';
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Sidebar badge                                                              */
/* ────────────────────────────────────────────────────────────────────────── */

/** Anything beyond this renders as "99+" in the sidebar, so counting past it is wasted work. */
const BADGE_CAP = 100;

/**
 * Backlog size for the sidebar badge. Fails soft to 0 — a missing RPC, a non-admin session
 * and a network blip all mean "nothing to show here", never a broken sidebar.
 *
 * Counts returned rows rather than issuing a `count: 'exact', head: true` query the way the
 * other three badges do, because the queue is an RPC, not a table this dashboard is allowed
 * to read directly. `p_limit` is capped at {@link BADGE_CAP} so the cost stays bounded no
 * matter how big the backlog gets; the badge renderer already collapses anything over 99
 * to "99+", so a bounded count is all the sidebar can display anyway.
 */
export async function countPendingTags(): Promise<number> {
  try {
    const { data, error } = await supabase.rpc(TAG_MODERATION_BINDING.queueRpc, {
      p_limit: BADGE_CAP,
      p_offset: 0,
    });
    if (error || !Array.isArray(data)) return 0;
    return data.length;
  } catch {
    return 0;
  }
}
